import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import Keycloak from 'keycloak-js';
import { keycloakConfig, keycloakInitOptions } from '../config/keycloak';

// Single source of truth for turning a parsed Keycloak token into our user
// shape. Used both at first login AND after every silent token refresh so that
// role/permission changes made in Keycloak take effect in the UI without a full
// page reload (the refreshed token already carries the new realm_access.roles).
const deriveUserFromToken = (tokenParsed) => {
  if (!tokenParsed) return null;
  return {
    id: tokenParsed.sub,
    email: tokenParsed.email,
    name: tokenParsed.name || tokenParsed.preferred_username,
    firstName: tokenParsed.given_name,
    lastName: tokenParsed.family_name,
    roles: tokenParsed.realm_access?.roles || [],
    groups: tokenParsed.groups || [],
  };
};

// Stable comparison so we only re-render when the *meaningful* identity/role
// content actually changes (a refresh mints a new token every minute).
const sameUser = (a, b) => {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.id === b.id &&
    a.email === b.email &&
    a.name === b.name &&
    JSON.stringify(a.roles || []) === JSON.stringify(b.roles || []) &&
    JSON.stringify(a.groups || []) === JSON.stringify(b.groups || []);
};

const CustomKeycloakContext = createContext({
  user: null,
  loading: false,
  error: null,
  isAuthenticated: false,
  sessionExpired: false,
  handleSessionExpired: () => {},
  login: () => {},
  logout: () => {},
  register: () => {},
  hasRole: () => false,
  hasGroup: () => false,
  getToken: () => null,
  refreshToken: () => Promise.resolve(null),
  keycloak: null,
});

export const CustomKeycloakProvider = ({ children }) => {
  const [keycloak, setKeycloak] = useState(null);
  const [loading, setLoading] = useState(true); // Start with loading true
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [initialized, setInitialized] = useState(false);



  const login = async () => {
    try {
      setLoading(true);
      setError(null);
      setSessionExpired(false);

      if (keycloak) {
        await keycloak.login({
          redirectUri: window.location.origin,
        });
      } else {
        console.error('Keycloak not initialized');
        setError('Authentication system not ready');
      }
    } catch (err) {
      console.error('Login failed:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const logout = useCallback(async () => {
    // 1) Always clear local auth state FIRST so the UI immediately reflects
    //    "signed out" — even if the redirect to Keycloak is slow or fails.
    setUser(null);
    setIsAuthenticated(false);
    setSessionExpired(false);
    setError(null);

    // 2) Clear any session-scoped artifacts we left behind.
    try {
      localStorage.removeItem('kc-callback');
      sessionStorage.removeItem('kc-callback');
    } catch (_) { /* storage may be locked down — safe to ignore */ }

    // 3) If Keycloak never initialized, just hard-redirect to home.
    if (!keycloak) {
      window.location.replace(window.location.origin + '/');
      return;
    }

    // 4) Prefer a manually-constructed logout URL with `id_token_hint` so
    //    Keycloak skips the "Do you want to log out?" confirmation page.
    try {
      if (
        keycloak.idToken &&
        keycloak.authServerUrl &&
        keycloak.realm &&
        keycloak.clientId
      ) {
        const redirectUri = encodeURIComponent(window.location.origin + '/');
        const logoutUrl =
          `${keycloak.authServerUrl}/realms/${keycloak.realm}` +
          `/protocol/openid-connect/logout` +
          `?client_id=${encodeURIComponent(keycloak.clientId)}` +
          `&post_logout_redirect_uri=${redirectUri}` +
          `&id_token_hint=${keycloak.idToken}`;
        window.location.assign(logoutUrl);
        return;
      }

      // 5) Fallback: let keycloak-js compose the logout URL itself.
      await keycloak.logout({ redirectUri: window.location.origin + '/' });
    } catch (err) {
      console.error('Keycloak logout failed, forcing local redirect:', err);
      // Last-resort hard reset — state is already cleared above.
      window.location.replace(window.location.origin + '/');
    }
  }, [keycloak]);

  const register = async () => {
    try {
      setLoading(true);
      setError(null);

      if (keycloak) {
        // Use login method with prompt=create for OIDC standard registration
        await keycloak.login({
          redirectUri: window.location.origin,
          prompt: 'create', // OIDC standard parameter for registration
        });
      } else {
        console.error('Keycloak not initialized');
        setError('Authentication system not ready');
      }
    } catch (err) {
      console.error('Registration failed:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const hasRole = (role) => {
    return user?.roles?.includes(role) || false;
  };

  const hasGroup = (group) => {
    return user?.groups?.includes(group) || false;
  };

  const getToken = () => {
    return keycloak?.token || null;
  };

  const refreshToken = async () => {
    if (keycloak) {
      try {
        await keycloak.updateToken(30);
        return keycloak.token;
      } catch (err) {
        console.error('Token refresh failed:', err);
        return null;
      }
    }
    return null;
  };

  const handleSessionExpired = () => {
    if (isAuthenticated) {
      console.warn('Session expired - triggering global handler');
      setSessionExpired(true);
    }
  };

  // Initialize Keycloak on component mount and handle callbacks
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setError(null);

        // Use the proper Keycloak configuration from config file
        const keycloakInstance = new Keycloak(keycloakConfig);

        // Initialize Keycloak with proper callback handling
        const authenticated = await keycloakInstance.init({
          ...keycloakInitOptions,
          onLoad: 'check-sso', // Check for existing SSO session
          checkLoginIframe: false, // Disable iframe check to avoid CSP violation in QA
        });
        
        // Handle token expiration event from Keycloak
        keycloakInstance.onTokenExpired = () => {
          console.log('Token expired event received - expecting automatic refresh');
          // We can try to update it immediately or let the automatic loop handle it
          // But it's safer to try updating it explicitly
          keycloakInstance.updateToken(30).catch(() => {
            console.error('Failed to refresh expired token');
            handleSessionExpired();
          });
        };

        console.log('Keycloak initialization result:', {
          authenticated,
          token: keycloakInstance.token ? 'Present' : 'Missing',
          tokenParsed: !!keycloakInstance.tokenParsed
        });

        // Whenever keycloak-js silently refreshes the access token, re-derive
        // the user from the *new* token so role/permission changes propagate to
        // the UI immediately (no reload). Without this, hasRole() and menu/route
        // gating stay frozen at the roles the user had at login.
        keycloakInstance.onAuthRefreshSuccess = () => {
          const next = deriveUserFromToken(keycloakInstance.tokenParsed);
          setUser((prev) => (sameUser(prev, next) ? prev : next));
        };

        // Set all state at once to prevent flickering
        if (authenticated) {
          // Extract user information from token
          const tokenParsed = keycloakInstance.tokenParsed;
          const userData = deriveUserFromToken(tokenParsed);

          // Set all authenticated state at once
          setKeycloak(keycloakInstance);
          setUser(userData);
          setIsAuthenticated(true);
          setSessionExpired(false);
          setInitialized(true);
          setLoading(false);
          
          console.log('User authenticated successfully:', tokenParsed?.preferred_username);

          console.log('COMPLETE TOKEN DATA FOR BYPASS:', JSON.stringify(tokenParsed, null, 2));
        } else {
          // Set all unauthenticated state at once
          setKeycloak(keycloakInstance);
          setUser(null);
          setIsAuthenticated(false);
          setInitialized(true);
          setLoading(false);
          
          console.log('User not authenticated');
        }

      } catch (err) {
        console.error('Failed to initialize Keycloak:', err);
        
        // Set all error state at once
        setError(err.message);
        setIsAuthenticated(false);
        setUser(null);
        setInitialized(true);
        setLoading(false);
      }
    };

    // Only initialize once
    if (!initialized && !keycloak) {
      initializeAuth();
    }
  }, [initialized, keycloak]);

  // Proactive token check loop
  useEffect(() => {
    if (!isAuthenticated || !keycloak) return;

    // Check token validity every 60 seconds
    const intervalId = setInterval(async () => {
      try {
        // minValidity = 30 means: if token expires in less than 30s, refresh it
        const refreshed = await keycloak.updateToken(30);
        if (refreshed) {
          console.log('Token refreshed proactively');
          // Re-sync roles/permissions from the freshly minted token so access
          // changes made in Keycloak reflect in the UI within one refresh cycle.
          const next = deriveUserFromToken(keycloak.tokenParsed);
          setUser((prev) => (sameUser(prev, next) ? prev : next));
        }
      } catch (err) {
        console.error('Proactive token check failed:', err);
        handleSessionExpired();
      }
    }, 60000); // 60 seconds

    return () => clearInterval(intervalId);
  }, [isAuthenticated, keycloak]);

  const value = useMemo(() => ({
    user,
    loading,
    error,
    isAuthenticated,
    sessionExpired,
    handleSessionExpired,
    login,
    logout,
    register,
    hasRole,
    hasGroup,
    getToken,
    refreshToken,
    keycloak,
  }), [user, loading, error, isAuthenticated, sessionExpired, keycloak]);

  return (
    <CustomKeycloakContext.Provider value={value}>
      {children}
    </CustomKeycloakContext.Provider>
  );
};

export const useCustomKeycloak = () => {
  const context = useContext(CustomKeycloakContext);
  if (!context) {
    throw new Error('useCustomKeycloak must be used within a CustomKeycloakProvider');
  }
  return context;
};